import { Middleware, combineReducers, configureStore } from '@reduxjs/toolkit';
import { loginApi } from '../api/loginApi';
import TokenSlice from './tokenSlice';
import SettingSlice from './settingSlice';

import {
  persistReducer,
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/es/storage';
import { othersApi } from '../api/otherApi';
import { contractApi } from '../api/contractApi';
import { afterUpdateCarMiddleware, carApi } from '../api/carApi';
import { driverApi } from '../api/driverApi';
import { userApi } from '../api/userApi';
import { companyApi } from '../api/companyApi';
import { afterUpdateWorkTimeMiddleware, workTimeApi } from '../api/workTimeApi';
import {
  afterDeleteCarAllocationMiddleware,
  carAllocationsApi,
} from '../api/carAllocationApi';
import ErrorSlice from './errorSlice';
import MaintenanceSlice from './maintenanceSlice';
import { UNAUTHORIZED } from '../api/handleErrorTags';
import { CarAllocationEditSlice } from './carAllocationEditSlice';
import { ConfirmAllocationTaskDeletionSlice } from './confirmAllocationTaskDeletionSlice';
import { fetchErrorHandlingMiddleware } from '../api/fetchError';

// API 追加時はこの配列に追加する。
// reducer, middleware の登録からログアウト時のキャッシュ削除まで対応してくれる。
const apiCollection = [
  loginApi,
  workTimeApi,
  othersApi,
  contractApi,
  carApi,
  driverApi,
  userApi,
  companyApi,
  carAllocationsApi,
] as const;
type ApiKeys = (typeof apiCollection)[number]['reducerPath'];

type ApiReducers = Record<ApiKeys, (typeof apiCollection)[number]['reducer']>;
const apiReducers = apiCollection.reduce((reducers, api) => {
  reducers[api.reducerPath] = api.reducer;
  return reducers;
}, {} as ApiReducers); // reduce による optional 除去のために as を例外的に許容する
// API 用の Reducer の型情報が欠落するが、API の Hook を介さず直接触ることはないので許容する。
const reducers = combineReducers({
  ...apiReducers,
  tokens: TokenSlice.reducer,
  settings: SettingSlice.reducer,
  error: ErrorSlice.reducer,
  maintenance: MaintenanceSlice.reducer,
  carAllocationEdit: CarAllocationEditSlice.reducer,
  confirmAllocationTaskDeletion: ConfirmAllocationTaskDeletionSlice.reducer,
});
const persistConfig = {
  key: 'root',
  version: 1,
  storage,
  whitelist: ['tokens'],
};

const persistedReducer = persistReducer(persistConfig, reducers);

const middlewares: Middleware[] = [
  ...apiCollection.map((api) => api.middleware),
  fetchErrorHandlingMiddleware,
  afterDeleteCarAllocationMiddleware,
  afterUpdateWorkTimeMiddleware,
  afterUpdateCarMiddleware,
];

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(...middlewares),
});

/**
 * クエリ系 API の認証エラータグを無効化しリフェッチを要求する
 */
export const invalidateUnauthorizedTags = () => {
  // 本プロジェクトでは RTK の API オブジェクトが別れているため、
  // Mutation に伴う自動 invalidateTags ではなくマニュアルの invalidatesTags を利用する。
  apiCollection.forEach((api) =>
    store.dispatch(api.util.invalidateTags([UNAUTHORIZED]))
  );
};

/**
 * ログアウト時操作
 */
export const resetStateOnLogout = () => {
  apiCollection.forEach((api) => {
    store.dispatch(api.util.resetApiState());
  });
  store.dispatch(TokenSlice.actions.resetTokens());
  store.dispatch(CarAllocationEditSlice.actions.resetAll());
};

export const persistor = persistStore(store);
export type AppState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
